/**
 * Resolves the legacy-flow triage tile source inputs: whether a report's config
 * is legacy, and — if so — the linked Django template plus the report's subjects
 * (each with its extracted jurisdictions). Tile-building + the analyst's
 * add-jurisdiction state live in the provider so they stay reactive; this hook
 * is purely the read-only fetch.
 *
 * Three fetches: the report config (is it legacy? which template?), the linked
 * Backoffice template (components, fetched live), and the RR's subjects
 * (extracted at SUBJECTS_EXTRACTED, carrying their jurisdictions). Idle for the
 * automated flow (no config / non-legacy → isLegacy=false).
 */

import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import type { TemplateTileSubject } from '@/components/NpAssignments/templateTiles';
import { vnextApiClient } from '@/services/vnext-api';
import type { BackofficeTemplate } from '@/types';

export interface LegacyTemplateSource {
  isLegacy: boolean;
  template: BackofficeTemplate | null;
  /** Subjects with their extracted jurisdictions (the analyst may add more). */
  subjects: TemplateTileSubject[];
  loading: boolean;
}

export function useLegacyTemplateTiles(
  reportRequestId: string | null,
  reportConfigId: string | null
): LegacyTemplateSource {
  const [isLegacy, setIsLegacy] = useState(false);
  const [template, setTemplate] = useState<BackofficeTemplate | null>(null);
  const [subjects, setSubjects] = useState<TemplateTileSubject[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reportConfigId || !reportRequestId) {
      setIsLegacy(false);
      setTemplate(null);
      setSubjects([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const config = await vnextApiClient.getReportConfig(reportConfigId);
        if (cancelled) {
          return;
        }
        if (!config.legacyFlow || config.backofficeTemplateId == null) {
          setIsLegacy(false);
          setTemplate(null);
          setSubjects([]);
          return;
        }
        setIsLegacy(true);
        const [tmpl, subs] = await Promise.all([
          vnextApiClient.getBackofficeTemplate(config.backofficeTemplateId),
          vnextApiClient.listTriageSubjects(reportRequestId),
        ]);
        if (cancelled) {
          return;
        }
        setTemplate(tmpl);
        setSubjects(
          subs.map((s) => ({
            id: s.id,
            name: s.subject_name,
            jurisdictions: (s.jurisdictions ?? []).map((j) => ({
              code: j.alpha2code,
              name: j.name || j.alpha2code,
            })),
          }))
        );
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportRequestId, reportConfigId]);

  return { isLegacy, template, subjects, loading };
}
